import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrDarkModal from 'client/components/SrDarkModal';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import { formatPhoneNumber } from 'common/formatters';
import { LISTING_PHONE_CONTACT_SUPPORT_FIELD_OPS } from 'common/listingConstants';

const SELF_TOUR_TROUBLESHOOT_STEPS = [
  {
    title: 'Restart the device',
    imageUrl: '/images/selfTours/self-tour-restart.jpeg',
    content: (
      <SrText
        color='grey'
        font='greycliff'
        weight='600'
        fontSize='16px'
        lineHeight='25px'
      >
        When you go to the Sunroom app and try to self-tour if the
        app tells you to call for access please restart the raspberry
        pi and wait about five minutes before trying to self-tour again.
      </SrText>
    ),
  },
  {
    title: 'Check the battery',
    imageUrl: '/images/selfTours/self-tour-sunlock-batteries.jpeg',
    content: (
      <ol className='orderedList'>
        <li key='sunlock-battery-low'>
          <SrText
            color='grey'
            font='greycliff'
            weight='600'
            fontSize='16px'
            lineHeight='25px'
          >
            Is it beeping 9 times quickly? If so replace batteries.
          </SrText>
        </li>
        <li key='sunlock-battery-depleted'>
          <SrText
            color='grey'
            font='greycliff'
            weight='600'
            fontSize='16px'
            lineHeight='25px'
          >
            Is it not making any sound or lighting up when pressing the buttons?
            If so replace batteries.
          </SrText>
        </li>
      </ol>
    ),
  },
  {
    title: 'Check WiFi USB Stick Light',
    imageUrl: '/images/selfTours/self-tour-stick.jpeg',
    content: (
      <SrFlex
        direction='col'
      >
        <SrBox>
          <SrText
            color='grey'
            font='greycliff'
            weight='600'
            fontSize='16px'
            lineHeight='25px'
          >
            For ATT Stick Only
          </SrText>
          <ul className='unorderedList'>
            <li key='sunlock-stick-light-green'>
              <SrText
                color='grey'
                font='greycliff'
                weight='600'
                fontSize='16px'
                lineHeight='25px'
              >
                Green light: Stick is working, no action needed.
              </SrText>
            </li>
            <li key='sunlock-stick-light-red'>
              <SrText
                color='grey'
                font='greycliff'
                weight='600'
                fontSize='16px'
                lineHeight='25px'
              >
                Red light: Not connected to the internet,
                unplug and plug back in, wait 3 mins, see if light turns green.
              </SrText>
            </li>
            <li key='sunlock-stick-light-none'>
              <SrText
                color='grey'
                font='greycliff'
                weight='600'
                fontSize='16px'
                lineHeight='25px'
              >
                No light: Stick needs to be reset take a pin and poke the reset
                hole in the back of the stick (Cover will need to be removed)
              </SrText>
            </li>
          </ul>
        </SrBox>
        <SrBox
          marginTop='10px'
        >
          <SrText
            color='grey'
            font='greycliff'
            weight='600'
            fontSize='16px'
            lineHeight='25px'
          >
            For ZTE Modem Only
          </SrText>
          <ul className='unorderedList'>
            <li key='sunlock-modem-light-blue'>
              <SrText
                color='grey'
                font='greycliff'
                weight='600'
                fontSize='16px'
                lineHeight='25px'
              >
                Blue blinking light: Stick is working, no action needed.
              </SrText>
            </li>
            <li key='sunlock-modem-light-none'>
              <SrText
                color='grey'
                font='greycliff'
                weight='600'
                fontSize='16px'
                lineHeight='25px'
              >
                No light: Stick isn’t working, needs to be replaced.
              </SrText>
            </li>
            <li key='sunlock-modem-light-red'>
              <SrText
                color='grey'
                font='greycliff'
                weight='600'
                fontSize='16px'
                lineHeight='25px'
              >
                Red light: Unplug and plug back in, wait 3 mins, see if light turns green.
              </SrText>
            </li>
          </ul>
        </SrBox>
      </SrFlex>
    ),
  },
  {
    title: 'Move Raspberry Pi closer to the door',
    imageUrl: '/images/selfTours/self-tour-move-raspberry.jpeg',
    content: (
      <SrText
        color='grey'
        font='greycliff'
        weight='600'
        fontSize='16px'
        lineHeight='25px'
      >
        Move Raspberry Pi as close to the door as possible and wait
        five minutes (ensure light on stick is showing connected status) and then self-tour.
      </SrText>
    ),
  },
  {
    title: 'Important Notes',
    content: (
      <SrFlex
        direction='col'
      >
        <ul className='unorderedList'>
          <li key='sunlock-important-notes'>
            <SrText
              color='grey'
              font='greycliff'
              weight='600'
              fontSize='16px'
              lineHeight='25px'
            >
              If restarting the Sunlock does not resolve the
              issue we may need to swap the wifi modem (usb wifi stick)
            </SrText>
          </li>
        </ul>
        <SrBox
          marginTop='24px'
        >
          <SrText
            color='grey'
            font='greycliff'
            weight='600'
            fontSize='16px'
            lineHeight='25px'
          >
            If unable to resolve, call support below.
          </SrText>
        </SrBox>
      </SrFlex>
    ),
  },
];

const SrSelfShowingTroubleshootModal = ({ handleCall, handleTourAgain }) => {
  return (
    <SrDarkModal>
      <div
        className={classNames(
          'SrListingSelfTouringSection sunlockStatusModal fieldopsTroubleshoot',
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <SrBox>
          <SrFlex
            direction='col'
            justify='center'
          >
            <SrText
              color='blueGreycliff'
              font='greycliff'
              weight='800'
              fontSize='21px'
              lineHeight='30px'
            >
              Sunlock not responding?
            </SrText>
            <SrBox
              marginTop='24px'
            >
              <SrText
                color='grey'
                font='greycliff'
                weight='600'
                fontSize='16px'
                lineHeight='18px'
              >
                There are a few reasons a Sunlock might not be working,
                below you will find multiple ways to detect and fix the issues.
                Try these steps and then press “Try Touring Again”.
                If unable to resolve call support at the number below.
              </SrText>
            </SrBox>
          </SrFlex>
          {SELF_TOUR_TROUBLESHOOT_STEPS.map(({ title, imageUrl = null, content }) => (
            <SrFlex
              direction='col'
              justify='center'
              marginTop='24px'
              key={title}
            >
              <SrText
                color='blueGreycliff'
                font='greycliff'
                weight='800'
                fontSize='18px'
                lineHeight='24px'
              >
                {title}
              </SrText>
              {
                imageUrl && (
                  <SrFlex
                    width='100%'
                    maxHeight='300px'
                    justify='center'
                    marginTop='10px'
                  >
                    <img
                      src={imageUrl}
                      alt={title}
                      width='100%'
                      className='troubleshootImage'
                    />
                  </SrFlex>
                )
              }
              <SrFlex
                marginTop='7px'
              >
                {content}
              </SrFlex>
            </SrFlex>
          ))}
          <SrFlex
            direction='col'
            justify='center'
            marginTop='24px'
            key='Contact support'
          >
            <SrText
              color='blueGreycliff'
              font='greycliff'
              weight='800'
              fontSize='18px'
              lineHeight='24px'
            >
              Contact support
            </SrText>
            <SrBox
              marginTop='7px'
            >
              <SrText
                color='blue300'
                font='greycliff'
                weight='800'
                fontSize='16px'
                lineHeight='18px'
                onClick={handleCall}
                cursor='pointer'
              >
                {formatPhoneNumber(LISTING_PHONE_CONTACT_SUPPORT_FIELD_OPS)}
              </SrText>
            </SrBox>
          </SrFlex>
        </SrBox>
        <div className='stickyBottom'>
          <SrButton
            theme={SR_BUTTON_THEME_ADOBE_GREEN}
            onClick={handleTourAgain}
          >
            Try Touring Again
          </SrButton>
        </div>
      </div>
    </SrDarkModal>
  );
};

SrSelfShowingTroubleshootModal.propTypes = {
  handleCall: PropTypes.func.isRequired,
  handleTourAgain: PropTypes.func.isRequired,
};

export default SrSelfShowingTroubleshootModal;
